/**
 * PatientPlanCard.tsx — what the PATIENT chose, on the screen where a clinician
 * decides.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY THIS EXISTS (CTO, 2026-09-13): "if they go into the plan level, the
 * clinician doesn't see what the patient has selected, and what they have
 * requested is currently not visible to the clinician."
 *
 * That was exactly right. The medication and term the patient bought were
 * rendered in ONE place — a `Help` line inside `PrescribeForm` — and
 * `PrescribeForm` only mounts AFTER the order is approved. At the moment of the
 * approve/reject decision the clinician saw the questionnaire, the photos, the
 * address and the safety acknowledgement, and nothing at all about which
 * medicine or which term they were being asked to approve.
 *
 * ⚠️ THIS IS THE PATIENT'S CHOICE, NOT A PRICING ANCHOR. `productKey` is what
 * they picked in the plan builder. It must never be confused with
 * `PLAN_ANCHOR_VARIANT` / `pricingAnchorVariant`, which is a pricing device that
 * carries a strength-shaped value and is deliberately walled off inside
 * orders-service — printing one of those as a clinical fact is the defect that
 * put a 10mg/0.6ml anchor on two signed prescriptions. Nothing anchor-shaped
 * reaches this component: it resolves a NAME from the plan catalogue and prints
 * the committed term, and there is no strength here at all because the patient
 * never chooses one.
 *
 * ⚠️ NO MONEY, EVER. Clinicians do not see figures ("clinicians never see
 * money"), and `PlanOption.plans` carries prices — so this renders
 * `medicationName`, `form` and the term, and reads nothing else off the option.
 * A price appearing here would also fail the money sweep in clinicians.spec.tsx.
 *
 * ⚠️ A MISSING NAME SAYS SO. If the catalogue no longer serves the ordered
 * `productKey` the card prints "Not recorded" rather than rendering nothing:
 * a silently absent plan line reads as "no plan was chosen", which is a
 * different and worse claim than "we could not resolve what they chose".
 */
import { useEffect, useState } from 'react';
import { getPlanOptions } from '../api/prices';
import type { PlanOption } from '../api/types';
import { Card, Help, Spinner } from '../ui/kit';
import { SectionTitle } from '../staff/ui';

/** The term, in the words the rest of the workspace uses. */
export function termWords(months: number | null | undefined): string | null {
  if (typeof months !== 'number' || !Number.isFinite(months) || months < 1) return null;
  return months === 1 ? '1 month' : `${months} months`;
}

export function PatientPlanCard({
  clinicSlug,
  productKey,
  months,
}: {
  clinicSlug: string;
  /** The order's `productKey` — the patient's own selection. */
  productKey: string | null | undefined;
  /** The order's committed term. */
  months: number | null | undefined;
}) {
  const [state, setState] = useState<{ state: 'loading' | 'ready' | 'error'; options: PlanOption[] }>({
    state: 'loading',
    options: [],
  });

  useEffect(() => {
    let live = true;
    setState({ state: 'loading', options: [] });
    getPlanOptions(clinicSlug)
      .then((res) => live && setState({ state: 'ready', options: res.options }))
      .catch(() => live && setState({ state: 'error', options: [] }));
    return () => {
      live = false;
    };
  }, [clinicSlug]);

  const term = termWords(months);
  const match = productKey ? state.options.find((o) => o.productKey === productKey) : undefined;

  return (
    <Card>
      <SectionTitle>What the patient chose</SectionTitle>
      {state.state === 'loading' ? (
        <Spinner label="Loading the plan…" />
      ) : (
        <p style={{ color: 'var(--gps-text)', fontSize: '14.5px', margin: 0 }}>
          {match ? `${match.medicationName} (${match.form})` : 'Not recorded'}
          {term ? ` · ${term}` : ''}
        </p>
      )}
      {/* The read FAILED — distinct from "the catalogue does not carry it", and
          the clinician needs to know which, because one is worth retrying and
          the other is a data problem on the order. */}
      {state.state === 'error' ? (
        <Help style={{ marginBottom: 0 }}>
          The plan catalogue could not be loaded, so the chosen medication cannot be named.
        </Help>
      ) : null}
      {state.state === 'ready' && !match ? (
        <Help style={{ marginBottom: 0 }}>
          This order names a plan the catalogue no longer carries. Check it before deciding.
        </Help>
      ) : null}
      <p style={{ color: 'var(--gps-muted)', fontSize: '12px', margin: 'var(--gps-s-2) 0 0' }}>
        The patient&rsquo;s own selection. They do not choose a strength &mdash; that is yours to set when you prescribe.
      </p>
    </Card>
  );
}
